import { Fragment, useMemo } from "react";
import type { Citation, FieldPack, LawStatus } from "../engine/types";
import type { RunResult } from "../engine/useRun";
import { Banner, Card, Chip, CopyButton, Divider } from "./primitives";
import type { ChipTone } from "./primitives";
import { Icon } from "./Icon";

/**
 * Renders a run result by `pack.output.kind`. One component, four shapes, no
 * field-id branching — the pack tells us the shape.
 *
 * Packs also embed an informal section protocol in the MODEL OUTPUT: a line
 * that is exactly `---SOMETHING---` separates the main answer from an ancillary
 * panel (translation's rationale, a summary's caveats, an explainer's quiz).
 * These are handled generically via a marker registry below: WHICH markers a
 * pack emits is data in the pack; HOW a marker looks is one registry entry.
 * A future pack that emits a registered marker gets its panel for free.
 */

// ─────────────────────────── Marker protocol ───────────────────────────

interface MarkerSpec {
  marker: string;
  title: string;
  icon: string;
  tone: ChipTone;
  /** Render body as light markdown (headings/bullets) rather than plain prose. */
  markdown?: boolean;
  /** Hide behind a "reveal" toggle — used for quiz answers so the reader tries first. */
  collapsible?: boolean;
  revealLabel?: string;
}

const MARKERS: MarkerSpec[] = [
  { marker: "---NOTES---", title: "شرح الخيارات", icon: "school", tone: "tertiary" },
  { marker: "---ملاحظات---", title: "ملاحظات على النص", icon: "flag", tone: "warn", markdown: true },
  { marker: "---شرح---", title: "شرح الصياغة", icon: "menu_book", tone: "tertiary", markdown: true },
  { marker: "---تحقق---", title: "تحقّق من فهمك", icon: "quiz", tone: "primary", markdown: true },
  {
    marker: "---الجواب---",
    title: "الأجوبة",
    icon: "key",
    tone: "primary",
    markdown: true,
    collapsible: true,
    revealLabel: "أظهر الأجوبة",
  },
];

interface Segment {
  spec: MarkerSpec | null;
  text: string;
}

/**
 * Split model output into a leading body plus any marker panels, preserving the
 * order markers appear in. A marker only counts when it sits alone on its line,
 * so a stray `---NOTES---` mid-sentence never fractures the text.
 */
function segmentOutput(output: string): { body: string; panels: Segment[] } {
  const lines = output.split("\n");
  const segments: Segment[] = [{ spec: null, text: "" }];
  const buf: string[][] = [[]];

  for (const line of lines) {
    const hit = MARKERS.find((m) => line.trim() === m.marker);
    if (hit) {
      segments.push({ spec: hit, text: "" });
      buf.push([]);
    } else {
      buf[buf.length - 1].push(line);
    }
  }

  const filled = segments.map((s, i) => ({ ...s, text: buf[i].join("\n").trim() }));
  return {
    body: filled[0]?.text ?? "",
    panels: filled.slice(1).filter((s) => s.text.length > 0),
  };
}

// ─────────────────────────── Light markdown ───────────────────────────

/** Just enough markdown for summaries and notes: headings, bullets, numbered
 *  lists, and **bold**. Not a full parser — packs emit a small, known subset. */
function inlineBold(text: string, keyBase: string): (string | React.ReactElement)[] {
  const out: (string | React.ReactElement)[] = [];
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  parts.forEach((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    if (m) out.push(<strong key={`${keyBase}-b${i}`}>{m[1]}</strong>);
    else if (p) out.push(p);
  });
  return out;
}

function Markdown({ text, className = "" }: { text: string; className?: string }) {
  const blocks = useMemo(() => {
    const lines = text.split("\n");
    type Block =
      | { kind: "h"; level: number; text: string }
      | { kind: "ul"; items: string[] }
      | { kind: "ol"; items: string[] }
      | { kind: "p"; text: string };
    const out: Block[] = [];
    let para: string[] = [];
    const flushPara = () => {
      if (para.length) {
        out.push({ kind: "p", text: para.join("\n") });
        para = [];
      }
    };
    for (const raw of lines) {
      const line = raw.trimEnd();
      const h = /^(#{1,4})\s+(.*)$/.exec(line);
      const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
      const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
      if (h) {
        flushPara();
        out.push({ kind: "h", level: h[1].length, text: h[2] });
      } else if (ul) {
        const last = out[out.length - 1];
        if (para.length) flushPara();
        if (last && last.kind === "ul") last.items.push(ul[1]);
        else out.push({ kind: "ul", items: [ul[1]] });
      } else if (ol) {
        const last = out[out.length - 1];
        if (para.length) flushPara();
        if (last && last.kind === "ol") last.items.push(ol[1]);
        else out.push({ kind: "ol", items: [ol[1]] });
      } else if (line.trim() === "") {
        flushPara();
      } else {
        para.push(line);
      }
    }
    flushPara();
    return out;
  }, [text]);

  return (
    <div className={`grid gap-2 ${className}`}>
      {blocks.map((b, i) => {
        if (b.kind === "h") {
          const cls = b.level <= 1 ? "md-title-large" : b.level === 2 ? "md-title-medium" : "md-title-small";
          return (
            <p key={i} className={`${cls} mt-2 text-on-surface first:mt-0`}>
              {inlineBold(b.text, `h${i}`)}
            </p>
          );
        }
        if (b.kind === "ul") {
          return (
            <ul key={i} className="grid list-disc gap-1.5 ps-6 marker:text-primary">
              {b.items.map((it, j) => (
                <li key={j} className="md-body-large text-on-surface">
                  {inlineBold(it, `u${i}-${j}`)}
                </li>
              ))}
            </ul>
          );
        }
        if (b.kind === "ol") {
          return (
            <ol key={i} className="grid list-decimal gap-1.5 ps-6 marker:text-primary">
              {b.items.map((it, j) => (
                <li key={j} className="md-body-large text-on-surface">
                  {inlineBold(it, `o${i}-${j}`)}
                </li>
              ))}
            </ol>
          );
        }
        return (
          <p key={i} className="prose-ar md-body-large text-on-surface">
            {inlineBold(b.text, `p${i}`)}
          </p>
        );
      })}
    </div>
  );
}

// ─────────────────────────── Marker panel ───────────────────────────

const PANEL_TONE: Record<ChipTone, { head: string; border: string }> = {
  neutral: { head: "bg-surface-c text-on-surface-variant", border: "border-outline-variant" },
  primary: { head: "bg-primary-container/60 text-on-primary-container", border: "border-primary/30" },
  tertiary: {
    head: "bg-tertiary-container/60 text-on-tertiary-container",
    border: "border-tertiary/40",
  },
  warn: { head: "bg-warn-container/70 text-on-warn-container", border: "border-warn/40" },
  success: { head: "bg-success-container/70 text-on-success-container", border: "border-success/40" },
  error: { head: "bg-error-container/70 text-on-error-container", border: "border-error/40" },
};

function MarkerPanel({ spec, text }: { spec: MarkerSpec; text: string }) {
  const tone = PANEL_TONE[spec.tone];
  const body = spec.markdown ? (
    <Markdown text={text} />
  ) : (
    <p className="prose-ar md-body-medium text-on-surface">{text}</p>
  );

  return (
    <Card variant="outlined" className={`overflow-hidden ${tone.border}`}>
      <div className={`flex items-center gap-2 px-5 py-3 ${tone.head}`}>
        <Icon name={spec.icon} size={20} filled />
        <h3 className="md-title-medium">{spec.title}</h3>
      </div>
      <div className="p-5">
        {spec.collapsible ? (
          <details className="group">
            <summary className="md-label-large flex cursor-pointer list-none items-center gap-1.5 text-primary">
              <Icon
                name="expand_more"
                size={20}
                className="transition-transform group-open:rotate-180"
              />
              {spec.revealLabel ?? "أظهر"}
            </summary>
            <div className="mt-3">{body}</div>
          </details>
        ) : (
          body
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────── Top-level dispatch ───────────────────────────

export function ResultView({ pack, result }: { pack: FieldPack; result: RunResult }) {
  const isRefusal = result.status === "refused";

  return (
    <div className="grid gap-4">
      {result.isDemo ? <DemoBanner /> : null}

      {isRefusal ? (
        <RefusalView pack={pack} result={result} />
      ) : pack.output.kind === "parallel" ? (
        <ParallelResult pack={pack} result={result} />
      ) : pack.output.kind === "cited" ? (
        <CitedResult pack={pack} result={result} />
      ) : (
        <ProseResult pack={pack} result={result} />
      )}
    </div>
  );
}

// ─────────────────────────── Demo & refusal ───────────────────────────

function DemoBanner() {
  return (
    <Banner tone="warn" icon="science" title="نتيجة تجريبية — مو حقيقية">
      هذا النظام قاعد يشتغل بوضع العرض بدون مفتاح النموذج، فالنتيجة أدناه <b>نموذج توضيحي</b> مو
      نتيجة حقيقية. لا تعتمد عليها. لتشغيل النتائج الحقيقية لازم يُضاف مفتاح Gemini على الخادم.
    </Banner>
  );
}

/**
 * A refusal is a CORRECT outcome (Law found no statute; the model declined to
 * guess). Rendered calmly — informational, not an angry red error — so the user
 * trusts that "I don't have this" is a real, honest answer.
 */
function RefusalView({ pack, result }: { pack: FieldPack; result: RunResult }) {
  return (
    <Card variant="outlined" className="overflow-hidden">
      <div className="flex items-start gap-3 bg-surface-c p-5">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-secondary-container text-on-secondary-container">
          <Icon name="info" size={24} filled />
        </div>
        <div className="min-w-0">
          <h3 className="md-title-medium text-on-surface">ما لكينا جواب موثوق</h3>
          <p className="md-body-small text-on-surface-variant">هذا رد صحيح — أفضل من تخمين.</p>
        </div>
      </div>
      <div className="p-5">
        <p className="prose-ar md-body-large text-on-surface">{result.output}</p>
        {pack.retrieval ? (
          <p className="md-body-medium mt-4 rounded-sm bg-surface-c p-3 text-on-surface-variant">
            <Icon name="lightbulb" size={16} className="me-1 align-middle text-tertiary" filled />
            جرّب تكتب اسم القانون أو رقم المادة إذا تعرفهم، أو راجع صفحة التغطية حتى تشوف شنو
            القوانين المتوفرة عدنا.
          </p>
        ) : null}
      </div>
    </Card>
  );
}

// ─────────────────────────── text / structured ───────────────────────────

function ProseResult({ pack, result }: { pack: FieldPack; result: RunResult }) {
  const { body, panels } = useMemo(() => segmentOutput(result.output), [result.output]);
  const structured = pack.output.kind === "structured";

  return (
    <div className="grid gap-4">
      <Card variant="elevated" className="overflow-hidden">
        <ResultHeader
          title={pack.output.title ?? "النتيجة"}
          copyable={pack.output.copyable !== false}
          // Copy the WHOLE output verbatim (markers and all) — for the letter,
          // the exact text including the blank ( ) fields is the deliverable.
          text={result.output}
        />
        <div className="p-5">
          {structured ? (
            <Markdown text={body} />
          ) : (
            <p className="prose-ar md-body-large text-on-surface">{body}</p>
          )}
        </div>
      </Card>

      {panels.map((p, i) =>
        p.spec ? <MarkerPanel key={`${p.spec.marker}-${i}`} spec={p.spec} text={p.text} /> : null,
      )}
    </div>
  );
}

// ─────────────────────────── parallel (translation) ───────────────────────────

function ParallelResult({ pack, result }: { pack: FieldPack; result: RunResult }) {
  const sourceName = pack.output.sourceInput;
  const source = sourceName && result.values ? String(result.values[sourceName] ?? "") : "";
  const { body: translation, panels } = useMemo(
    () => segmentOutput(result.output),
    [result.output],
  );

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 lg:grid-cols-2">
        {source ? (
          <Card variant="filled" className="overflow-hidden">
            <div className="flex items-center gap-2 px-5 pt-4 pb-2 text-on-surface-variant">
              <Icon name="article" size={18} />
              <span className="md-label-large">النص الأصلي</span>
            </div>
            <Divider />
            <div className="p-5">
              <p className="prose-ar md-body-large text-on-surface-variant">{source}</p>
            </div>
          </Card>
        ) : null}

        <Card variant="elevated" className="overflow-hidden">
          <ResultHeader
            title={pack.output.title ?? "الترجمة"}
            copyable={pack.output.copyable !== false}
            text={translation}
            accent
          />
          <div className="p-5">
            <p className="prose-ar md-body-large text-on-surface">{translation}</p>
          </div>
        </Card>
      </div>

      {panels.map((p, i) =>
        p.spec ? <MarkerPanel key={`${p.spec.marker}-${i}`} spec={p.spec} text={p.text} /> : null,
      )}
    </div>
  );
}

// ─────────────────────────── cited (law) ───────────────────────────

const STATUS_META: Record<LawStatus, { tone: ChipTone; icon: string; note: string }> = {
  نافذ: { tone: "success", icon: "check_circle", note: "هذا النص نافذ ومطبّق." },
  معدل: {
    tone: "warn",
    icon: "edit_note",
    note: "هذا النص معدّل — انتبه، ممكن تكون في نسخة أحدث للمادة.",
  },
  ملغي: {
    tone: "error",
    icon: "cancel",
    note: "هذا النص ملغي — مو قانون نافذ. لا تعتمد عليه كقانون حالي.",
  },
  "غير معروف": {
    tone: "neutral",
    icon: "help",
    note: "حالة هذا النص غير مؤكدة عدنا — تأكد قبل الاعتماد عليه.",
  },
};

function StatusChip({ status }: { status: LawStatus }) {
  const meta = STATUS_META[status];
  return (
    <Chip tone={meta.tone} icon={meta.icon} filled>
      {status}
    </Chip>
  );
}

/**
 * Splits the answer on inline [n] markers and turns each into an anchor to its
 * citation, colored by that citation's status so a repealed-law reference reads
 * as dangerous even at a glance.
 */
function AnswerWithMarkers({ text, citations }: { text: string; citations: Citation[] }) {
  const parts = useMemo(() => text.split(/(\[\d+\])/g), [text]);
  return (
    <p className="prose-ar md-body-large text-on-surface">
      {parts.map((part, i) => {
        const m = /^\[(\d+)\]$/.exec(part);
        if (!m) return <Fragment key={i}>{part}</Fragment>;
        const n = Number(m[1]);
        const cite = citations[n - 1];
        const tone: ChipTone = cite ? STATUS_META[cite.status].tone : "primary";
        const toneClass =
          tone === "error"
            ? "bg-error-container text-on-error-container"
            : tone === "warn"
              ? "bg-warn-container text-on-warn-container"
              : tone === "success"
                ? "bg-success-container text-on-success-container"
                : "bg-primary-container text-on-primary-container";
        return (
          <a
            key={i}
            href={`#cite-${n}`}
            className={`md-label-medium mx-0.5 inline-flex items-center rounded-xs px-1.5 align-baseline no-underline ${toneClass}`}
            aria-label={`المصدر رقم ${n}`}
          >
            <bdi>[{n}]</bdi>
          </a>
        );
      })}
    </p>
  );
}

function CitationCard({ n, citation }: { n: number; citation: Citation }) {
  const meta = STATUS_META[citation.status];
  const dangerous = citation.status === "ملغي";
  return (
    <div
      id={`cite-${n}`}
      className={[
        "scroll-mt-24 rounded-md border p-4",
        dangerous ? "border-error bg-error-container/30" : "border-outline-variant bg-surface-lowest",
      ].join(" ")}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="md-label-large grid h-6 min-w-6 place-items-center rounded-full bg-secondary-container px-1.5 text-on-secondary-container">
          <bdi>{n}</bdi>
        </span>
        <StatusChip status={citation.status} />
        {citation.article ? (
          <Chip tone="neutral" icon="article">
            المادة <bdi>{citation.article}</bdi>
          </Chip>
        ) : null}
      </div>

      <h4 className="md-title-small text-on-surface">{citation.lawTitle}</h4>
      <p className="md-body-medium mt-0.5 text-on-surface-variant">
        رقم <bdi>{citation.lawNumber}</bdi> لسنة <bdi>{citation.year}</bdi>
      </p>

      <p
        className={`md-body-small mt-2 flex items-start gap-1.5 ${
          dangerous ? "text-error" : "text-on-surface-variant"
        }`}
      >
        <Icon name={meta.icon} size={15} className="mt-0.5 shrink-0" filled />
        <span>{meta.note}</span>
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {citation.sourceUrl ? (
          <a
            href={citation.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="md-label-large inline-flex items-center gap-1 text-primary hover:underline"
          >
            <Icon name="open_in_new" size={16} />
            النص المصدر
          </a>
        ) : null}
        {citation.lastFetched ? (
          <span className="md-body-small text-on-surface-variant">
            آخر تحديث: <bdi>{citation.lastFetched}</bdi>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CitedResult({ pack, result }: { pack: FieldPack; result: RunResult }) {
  const citations = result.citations ?? [];
  const hasRepealed = citations.some((c) => c.status === "ملغي");
  const hasAmended = citations.some((c) => c.status === "معدل");

  return (
    <div className="grid gap-4">
      {hasRepealed ? (
        <Banner tone="error" icon="gavel" title="تنبيه: في نص ملغي بالمصادر">
          واحد أو أكثر من النصوص أدناه <b>ملغي</b> ومو قانون نافذ. تأكد من حالة كل مادة قبل ما
          تعتمد عليها.
        </Banner>
      ) : hasAmended ? (
        <Banner tone="warn" icon="edit_note" title="في نص معدّل بالمصادر">
          بعض النصوص أدناه <b>معدّلة</b> — ممكن تكون في صيغة أحدث. راجع حالة كل مادة.
        </Banner>
      ) : null}

      <Card variant="elevated" className="overflow-hidden">
        <ResultHeader
          title={pack.output.title ?? "الجواب"}
          copyable={pack.output.copyable !== false}
          text={result.output}
          accent
        />
        <div className="p-5">
          {citations.length > 0 ? (
            <AnswerWithMarkers text={result.output} citations={citations} />
          ) : (
            <p className="prose-ar md-body-large text-on-surface">{result.output}</p>
          )}
        </div>
      </Card>

      <div>
        <div className="mb-2 flex items-center gap-2 px-1 text-on-surface-variant">
          <Icon name="menu_book" size={20} />
          <h3 className="md-title-medium">المصادر</h3>
          <span className="md-body-small">
            (<bdi>{citations.length}</bdi>)
          </span>
        </div>

        {citations.length > 0 ? (
          <div className="grid gap-3">
            {citations.map((c, i) => (
              <CitationCard key={`${c.lawNumber}-${i}`} n={i + 1} citation={c} />
            ))}
          </div>
        ) : (
          <Card variant="filled" className="p-4">
            <p className="md-body-medium text-on-surface-variant">
              ما في مصادر مرفقة بهذا الجواب. بالنسبة لأداة قانونية، جواب بلا مصدر لازم يُتعامل
              معه بحذر شديد.
            </p>
          </Card>
        )}
      </div>

      <p className="md-body-small flex items-start gap-1.5 rounded-sm bg-surface-c p-3 text-on-surface-variant">
        <Icon name="balance" size={16} className="mt-0.5 shrink-0" />
        <span>
          هذي نتيجة بحث بالنصوص القانونية — <b>مو استشارة قانونية</b>. القرار بحالتك يحتاج محامي.
        </span>
      </p>
    </div>
  );
}

// ─────────────────────────── shared header ───────────────────────────

function ResultHeader({
  title,
  copyable,
  text,
  accent = false,
}: {
  title: string;
  copyable: boolean;
  text: string;
  accent?: boolean;
}) {
  return (
    <>
      <div
        className={`flex items-center justify-between gap-2 px-5 py-3 ${
          accent ? "bg-primary-container/60 text-on-primary-container" : "text-on-surface"
        }`}
      >
        <h3 className="md-title-medium">{title}</h3>
        {copyable ? <CopyButton text={text} /> : null}
      </div>
      <Divider />
    </>
  );
}
